import React from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import {
    CatalogContent,
    CatalogControls,
    CatalogGrid,
    PaddedContent,
    ConfigureIntro,
    CreationFooter,
    FormCard,
    StepIndicator,
} from "components/organisms/CreationModal";
import {
    SourceCreationFooter,
    SourceCreationForm,
} from "components/organisms/SourceCreation";
import {
    TrackerStepContent,
    TrackerStepFooter,
} from "components/organisms/TrackerStep";

import { Step } from "./CreateSourceModal.types";

export const getStepLabels = (language) => [
    language?.source_modal?.choose_source ?? "Choose source",
    language?.source_modal?.configure ?? "Configure",
];

export const getCreateTitle = (language) =>
    language?.source_modal?.create_source ?? "Create source";

export const getCreateSubtitle = (language) =>
    language?.source_modal?.choose_source_subtitle ??
    "Choose where your signals come from.";

/**
 * `GenericModal` header props (title, subtitle, icon, header slots) for the
 * current step of the Create Source modal.
 */
export const getCreateSourceHeaderProps = ({
    step,
    language,
    catalog,
    isMappingView,
    canGoBack,
    onBack,
}) => {
    const stepLabels = getStepLabels(language);
    const createTitle = getCreateTitle(language);

    if (step === Step.CATALOG) {
        return {
            title: createTitle,
            subtitle: getCreateSubtitle(language),
            headerCenter: <StepIndicator steps={stepLabels} activeIndex={0} />,
            headerBottom: (
                <CatalogControls
                    catalog={catalog}
                    allLabel={
                        language?.source_modal?.all_sources ?? "All sources"
                    }
                    searchPlaceholder={
                        language?.source_modal?.search_sources ??
                        "Search sources…"
                    }
                />
            ),
        };
    }

    if (step === Step.INFO) {
        return {
            title: createTitle,
            subtitle: getCreateSubtitle(language),
            headerCenter: (
                <StepIndicator
                    steps={stepLabels}
                    activeIndex={1}
                    onStepClick={
                        canGoBack && !isMappingView ? onBack : undefined
                    }
                />
            ),
        };
    }

    return { title: createTitle, subtitle: getCreateSubtitle(language) };
};

/** Modal body for the current step. */
export const renderCreateSourceContent = ({
    step,
    language,
    catalog,
    selectedItem,
    pendingType,
    onPendingTypeChange,
    onConfirmType,
    isCatalogLoading,
    sourceCreation,
    isMappingView,
    trackers,
    trackerStep,
}) => {
    if (step === Step.CATALOG) {
        return (
            <CatalogContent>
                <CatalogGrid
                    catalog={catalog}
                    value={pendingType}
                    onChange={onPendingTypeChange}
                    onConfirm={onConfirmType}
                    isLoading={isCatalogLoading}
                    emptyTitle={
                        language?.source_modal?.no_catalog_results ??
                        "Nothing matches your search."
                    }
                />
            </CatalogContent>
        );
    }

    if (step === Step.INFO) {
        if (isMappingView) {
            return <SourceCreationForm sourceCreation={sourceCreation} />;
        }
        const typeName = selectedItem?.name ?? "";
        return (
            <PaddedContent>
                <ConfigureIntro
                    image={selectedItem?.image}
                    name={typeName}
                    title={`${language?.source_modal?.configure ?? "Configure"} ${typeName}`.trim()}
                    description={selectedItem?.description}
                />
                <FormCard>
                    <SourceCreationForm sourceCreation={sourceCreation} />
                </FormCard>
            </PaddedContent>
        );
    }

    return <TrackerStepContent trackers={trackers} trackerStep={trackerStep} />;
};

/** Modal footer for the current step; `null` when the step has none. */
export const renderCreateSourceFooter = ({
    step,
    language,
    selectedItem,
    pendingType,
    onConfirmType,
    handleClose,
    sourceCreation,
    isMappingView,
    canGoBack,
    onBack,
    trackerStep,
}) => {
    const statusLabel =
        selectedItem &&
        `${selectedItem.name} ${language?.source_modal?.selected ?? "selected"}`;

    if (step === Step.CATALOG) {
        return (
            <CreationFooter
                onCancel={handleClose}
                cancelLabel={language?.buttons?.cancel ?? "Cancel"}
                statusLabel={statusLabel}
                onSubmit={() => onConfirmType()}
                submitLabel={language?.buttons?.continue ?? "Continue"}
                submitEndIcon={<ArrowForwardIcon />}
                isSubmitDisabled={!pendingType}
                submitTestId="create-source-continue"
            />
        );
    }

    if (step === Step.INFO && !isMappingView) {
        return (
            <SourceCreationFooter
                sourceCreation={sourceCreation}
                onBack={canGoBack ? onBack : undefined}
            />
        );
    }

    if (step === Step.INFO) {
        // Post-create mapping: Save is registered by `SourceMappingArea`.
        const action = sourceCreation.footerAction;
        return (
            <CreationFooter
                onCancel={handleClose}
                cancelLabel={language?.buttons?.cancel ?? "Cancel"}
                isCancelDisabled={Boolean(action?.isLoading)}
                onSubmit={action?.onSubmit}
                submitLabel={action?.label ?? language?.buttons?.save ?? "Save"}
                isSubmitDisabled={Boolean(action?.isDisabled)}
                isSubmitLoading={Boolean(action?.isLoading)}
                hideSubmit={!action}
                submitTestId="source-mapping-save"
            />
        );
    }

    return <TrackerStepFooter language={language} trackerStep={trackerStep} />;
};
